import axios from 'axios';
import { HttpErrorResponse } from '@trucktrax/trucktrax-ts-common';
import recordLog from '../util/errorUtil';
import { store } from '../Main';
import { getTokens, isExpired, setTokens } from '../util/authUtil';
import { noop } from '../util/appUtil';
import { newAccessToken } from '../store/actions/authActions';
import { logOut } from './authService';
import { AUTH_ERROR, AUTH_USER } from '../constants/actionConstants';
import {
  GENERAL_ERROR_HANDLER_TYPE,
  SESSION_EXPIRED,
  USER_UNAUTHORIZED,
} from '../constants/errorConstants';
import {
  CANCEL_API_DUE_TO_PAGE_CHANGE,
  INVALID_STALE_TOKEN_LOGOUT,
  CLIENT_ID,
  WCS,
} from '../constants/appConstants';
import { getAuthBaseUrl } from '../util/apiUtil';

interface DownloadFileResult {
  data: BlobPart;
}

// Logs out without throwing, for callers that report failure via return value.
// REST retry chains use forceLogOut (below), which throws to abort the chain.
export function dispatchLogOut(message: string) {
  store.dispatch(logOut() as any);
  store.dispatch({
    type: AUTH_ERROR,
    payload: message,
  });
  store.dispatch(newAccessToken(false));
}

export function forceLogOut(message: string) {
  dispatchLogOut(message);
  throw INVALID_STALE_TOKEN_LOGOUT;
}

async function waitForNewToken() {
  return new Promise(p => {
    setTimeout(p, 1000);
  });
}

// How waiting on an in-flight refresh (started by another caller) resolved:
// - idle:    nothing was in flight
// - settled: a concurrent refresh completed while we waited
// - stuck:   still pending after ~10s (treat as failed/dead)
type PendingRefreshWait = 'idle' | 'settled' | 'stuck';

// Owns the whole pending-refresh check so callers don't read the flag directly.
async function waitForPendingRefresh(): Promise<PendingRefreshWait> {
  if (!store.getState().newTokenRequest.pending) {
    return 'idle';
  }
  let retryCount = 0;
  while (retryCount < 10) {
    retryCount += 1;

    await waitForNewToken();
    if (!store.getState().newTokenRequest.pending) {
      return 'settled';
    }
  }
  return 'stuck';
}

// POSTs the refresh grant and stores the new tokens. Shared by the REST and
// socket refresh paths; throws on failure so each caller can react its own way.
async function requestAndStoreNewTokens(rtk: string | null): Promise<void> {
  const url = getAuthBaseUrl();
  const { sessionId, authTenantName } = store.getState();

  const { data } = await axios.post(
    url,
    {},
    {
      params: {
        client_id: CLIENT_ID,
        grant_type: 'refresh_token',
        refresh_token: rtk,
        userType: 'user',
        tenant_name: authTenantName,
      },
      headers: {
        Authorization: `Basic ${WCS}`,
        sessionId: sessionId || '',
      },
    },
  );
  setTokens((action: any) => store.dispatch(action), data, sessionId ?? '');
  store.dispatch({ type: AUTH_USER });
  store.dispatch(newAccessToken(false));
}

// A refresh POST failing with 400/401/403 means the refresh token itself was
// rejected -- the session is dead. Anything else (5xx, network, timeout) is a
// transient auth-service problem the session can outlive. See DEV-4179.
function isRefreshTokenRejected(error: any): boolean {
  const status = error?.response?.status;
  return status === 400 || status === 401 || status === 403;
}

export async function refreshAccessToken(
  args: any,
  retry: (...args: any) => void = noop,
): Promise<any> {
  // Retry only if a concurrent refresh settled *and* left a usable token. If it
  // settled without one (the other refresh failed/logged out), fall through to
  // our own attempt rather than retrying into a guaranteed 401. idle/stuck also
  // fall through.
  if ((await waitForPendingRefresh()) === 'settled') {
    const { accessToken } = getTokens();
    if (accessToken && !isExpired(accessToken)) {
      return retry(...args);
    }
  }

  store.dispatch(newAccessToken(true));

  const { refreshToken: rtk } = getTokens();
  if (isExpired(rtk ?? '')) return forceLogOut(SESSION_EXPIRED);

  try {
    await requestAndStoreNewTokens(rtk);
    return retry(...args);
  } catch (e: any) {
    if (e === INVALID_STALE_TOKEN_LOGOUT) {
      throw e;
    }
    if (isRefreshTokenRejected(e)) {
      return forceLogOut(USER_UNAUTHORIZED);
    }
    // Transient auth failure: don't log out (that disconnects sockets and
    // re-hits the struggling auth server, DEV-4179). Clear the in-flight flag
    // and surface the error so the request rejects with the session intact.
    store.dispatch(newAccessToken(false));
    throw e;
  }
}

/**
 * How a token refresh resolved:
 * - Refreshed: new tokens stored; caller may reconnect now.
 * - Invalid: refresh token rejected/expired; user has been logged out. Stop.
 * - Transient: auth service unreachable or 5xx; session may still be valid, so
 *   keep backing off rather than logging out.
 */
export const RefreshOutcome = {
  Refreshed: 'refreshed',
  Invalid: 'invalid',
  Transient: 'transient',
} as const;

export type TokenRefreshOutcome = (typeof RefreshOutcome)[keyof typeof RefreshOutcome];

// Refresh the token and report the outcome, without a request to retry.
// Distinct from refreshAccessToken (which logs out on any failure) so the
// socket paths can back off through an auth outage instead of logging every
// dispatcher out to a login screen that may itself be down. See DEV-4179.
export async function ensureFreshAccessToken(): Promise<TokenRefreshOutcome> {
  const pendingWait = await waitForPendingRefresh();
  if (pendingWait === 'settled') {
    const { accessToken, refreshToken } = getTokens();
    if (accessToken && !isExpired(accessToken)) {
      return RefreshOutcome.Refreshed;
    }
    // Settled but left no usable access token. If it ended in logout (which
    // clears both tokens) report invalid so callers stop instead of scheduling
    // a doomed reconnect; otherwise it's a transient miss.
    return !refreshToken || isExpired(refreshToken)
      ? RefreshOutcome.Invalid
      : RefreshOutcome.Transient;
  }
  if (pendingWait === 'stuck') {
    return RefreshOutcome.Transient;
  }

  // 'idle' -- nothing was in flight, so do our own refresh.
  store.dispatch(newAccessToken(true));

  const { refreshToken: rtk } = getTokens();
  if (isExpired(rtk ?? '')) {
    dispatchLogOut(SESSION_EXPIRED);
    return RefreshOutcome.Invalid;
  }

  try {
    await requestAndStoreNewTokens(rtk);
    return RefreshOutcome.Refreshed;
  } catch (e: any) {
    if (isRefreshTokenRejected(e)) {
      dispatchLogOut(USER_UNAUTHORIZED);
      return RefreshOutcome.Invalid;
    }
    store.dispatch(newAccessToken(false));
    return RefreshOutcome.Transient;
  }
}

function errorHandler(url: string) {
  return (error: HttpErrorResponse) => {
    const status = error.response && error.response.status;
    if (status === 401) {
      forceLogOut(USER_UNAUTHORIZED);
    } else if (axios.isCancel(error)) {
      throw CANCEL_API_DUE_TO_PAGE_CHANGE;
    } else {
      recordLog(GENERAL_ERROR_HANDLER_TYPE, url, error);
      throw error;
    }
  };
}

export const request = axios.create({
  paramsSerializer: params =>
    Object.keys(params)
      .map(k => `${k}=${params[k]}`)
      .join('&'),
});

request.interceptors.request.use(axiosConfig => {
  const { tenantUrl } = getTokens();
  if (axiosConfig.headers) {
    axiosConfig.headers.Authorization = axiosConfig.params.tk;
    axiosConfig.headers['tenant-url'] = tenantUrl || '';
  }
  return axiosConfig;
});

export function getRequest(url: string, params?: any, cancelToken?: any) {
  const { accessToken: tk } = getTokens();
  if (isExpired(tk ?? '')) return refreshAccessToken(arguments, getRequest);
  return request
    .get(url, {
      params: { tk, ...params },
      cancelToken,
    })
    .catch(errorHandler(url));
}

export function getRequestWhenTokenPresentInUrl(url: string, params = {}) {
  return request.get(url, { params }).catch(errorHandler(url));
}

export function postRequest(url: string, body?: any, params?: any) {
  const { accessToken: tk } = getTokens();
  if (isExpired(tk ?? '')) return refreshAccessToken(arguments, postRequest);
  return request.post(url, body, { params: { tk, ...params } }).catch(errorHandler(url));
}

export function putRequest(url: string, body?: any, params?: any, options = {}) {
  const { accessToken: tk } = getTokens();
  if (isExpired(tk ?? '')) return refreshAccessToken(arguments, putRequest);
  return request.put(url, body, { params: { tk, ...params }, ...options }).catch(errorHandler(url));
}

export function deleteRequest(url: string, params?: any) {
  const { accessToken: tk } = getTokens();
  if (isExpired(tk ?? '')) return refreshAccessToken(arguments, deleteRequest);
  return request.delete(url, { params: { tk, ...params } }).catch(errorHandler(url));
}

export async function downloadFile(
  url: string,
  params?: any,
  filename?: string,
  contentType?: string,
  openInBrowser?: boolean,
) {
  const { accessToken: tk } = getTokens();
  if (tk && isExpired(tk)) {
    return refreshAccessToken([url, params, filename, contentType, openInBrowser], downloadFile);
  }

  const response = (await request
    .get(url, {
      responseType: 'blob',
      params: { tk, ...params },
    })
    .catch(errorHandler(url))) as DownloadFileResult;

  const blob = new Blob([response.data], { type: contentType });
  const blobUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  if (!openInBrowser) {
    anchor.download = filename || url;
  } else {
    anchor.target = '_blank';
  }
  anchor.href = blobUrl;
  anchor.click();
  return response.data;
}

export function postFile(url: string, value: Blob, fileName: string, params?: any) {
  const { accessToken: tk } = getTokens();
  const { currentRegion } = store.getState();
  const currentRegionUrl = currentRegion.url;

  if (tk && isExpired(tk)) {
    return refreshAccessToken(arguments, postFile);
  }

  const formData = new FormData();
  formData.append('file', value, fileName);

  return request.post(url, formData, {
    params: {
      tk,
      region: currentRegionUrl,
      ...params,
    },
  });
}
